import React, { useEffect, useLayoutEffect, useState, useMemo, useCallback, useRef } from 'react';
import MainWrapper from '../wrappers/mainWrapper';
import CollapsedMatchBlock from '../restructured/CollapsedMatchBlock';
import ExpandedMatchBlock from '../restructured/ExpandedMatchBlock';
import { useParams } from 'react-router-dom';
import '../../css/restructuredTransitions.css';
import PDFViewer from '../restructured/PDFViewer';
import Filters from '../restructured/Filters';
import ZoneFilter from '../restructured/ZoneFilter';
import GoIcon from '../../img/_Go.png';
import yieldIcon from '../../img/_Yield.png';
import PDFExport from '../restructured/assets/export-icon.svg';
import NukeButton from '../restructured/NukeButton';
import revertMatch from '../restructured/functions/revertMatch';
import toggleExpanded from '../restructured/functions/toggleExpanded';
import { fetchData } from '../restructured/functions/fetchData';
import saveMatchResults from '../restructured/functions/saveMatchResults';
import saveMatchNotes from '../restructured/functions/saveMatchNotes';
import saveMarkForExport from '../restructured/functions/saveMarkForExport';
import { revertAllMatches } from '../restructured/functions/revertAllMatches';
import { saveLock } from '../restructured/functions/saveLock';
import collapseIcon from '../restructured/assets/collapse-icon.png';
import expandIcon from '../restructured/assets/expand-icon.png';
import { useSelector } from 'react-redux';
import pdfIcon from '../restructured/assets/pdf-light.png';

const RestructuredResults = () => {
  const { comparisonID } = useParams();
  const userData = useSelector(state => state.user)
  const [data, setData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState('loading-spinner');
  const [visibleDocs, setVisibleDocs] = useState({ doc1: false, doc2: false });
  const [designationFilter, setDesignationFilter] = useState('All');
  const [zoneFilter, setZoneFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectAllForExport, setSelectAllForExport] = useState(false);
  const [selectAllExpanded, setSelectAllExpanded] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingExpand, setLoadingExpand] = useState(false);
  const [loadingCsvExport, setLoadingCsvExport] = useState(false);
  const [stackSimilarItems, setStackSimilarItems] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const hasTrialBanner = userData && userData.trial_over === false;
  const resultsTopOffset = hasTrialBanner ? 116 : 86;
  const pageSize = 25;
  const [currentPage, setCurrentPage] = useState(1);
  const [unmatchedExpandedMap, setUnmatchedExpandedMap] = useState({});
  const [unmatchedExportMap, setUnmatchedExportMap] = useState({});
  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const paginationRef = useRef(null);

  useEffect(() => {
    fetchData(comparisonID, setData, setLoadingStatus);
  }, [comparisonID]);

  useEffect(() => {
    if (!userData?._id || !comparisonID) return;
    const fetchSignedUrl = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_GET_PDFS}/${userData._id}/${comparisonID}.pdf`
        );
        if (!res.ok) {
          setSignedUrl(null);
          return;
        }
        const awsdata = await res.json();
        if (awsdata?.url) {
          setSignedUrl(awsdata.url);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch signed PDF URL:', err);
        setSignedUrl(null);
      }
    };  
    fetchSignedUrl();
    const intervalId = setInterval(fetchSignedUrl, 10000);
    return () => clearInterval(intervalId);
  }, [userData?._id, comparisonID]);

  const saveResults = useCallback(async (resultsID, updatedResults) => {
    if (!data) return;
    const fullUpdatedData = { ...data, results: updatedResults };
    try {
      const response = await fetch(`${process.env.REACT_APP_UPDATE_RESULTS}/${resultsID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: fullUpdatedData }),
      });
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.message || 'Failed to save results');
      }
    } catch (err) {
      console.error('❌ Error saving results:', err);
    }
  }, [data]);

  const lineItemMap = useMemo(() => {
    if (!data?.cleanParsedEstData?.lineItems) return {};
    const map = {};
    for (const li of data.cleanParsedEstData.lineItems) {
      const id = Object.keys(li)[0];
      map[id] = li[id];
    }
    return map;
  }, [data?.cleanParsedEstData?.lineItems]);

  const insLineItemMap = useMemo(() => {
    if (!data?.cleanParsedInsData?.lineItems) return {};
    const map = {};
    for (const li of data.cleanParsedInsData.lineItems) {
      const id = Object.keys(li)[0];
      map[id] = li[id];
    }
    return map;
  }, [data?.cleanParsedInsData?.lineItems]);

  const getDescriptionFromLineItem = useCallback((lineItem) => {
    const values = lineItem?.values || {};
    return (
      values.DESCRIPTION ||
      values.Description ||
      values.DESC ||
      Object.values(values)[0] ||
      ''
    );
  }, []);

  const getScopeStackLabel = useCallback((description) => {
    const text = (description || '').toLowerCase();
    if (!text) return 'Other Scope';

    if (/(tear\s*out|tearout|demol|remove|unsalvageable|cut\s*&?\s*bag)/i.test(text)) {
      return 'Tear Out / Demolition';
    }
    if (/(mitigation|dry(ing|out)?|dehu|air mover|moisture|water extraction|containment)/i.test(text)) {
      return 'Mitigation / Dryout';
    }
    if (/(rem\/reset|reset|re-?set|detach|reinstall)/i.test(text)) {
      return 'Reset / Reinstall';
    }
    if (/(replace|install|supply|underlayment|subfloor|insulation|drywall|cabinet|door|casing|base molding)/i.test(text)) {
      return 'Replace / Install';
    }
    if (/(clean|cleaning|remediation|sanitize|decontamination|final clean)/i.test(text)) {
      return 'Cleaning / Remediation';
    }
    if (/(prime|paint|stain|finish|texture)/i.test(text)) {
      return 'Paint / Finish';
    }
    if (/(carpet|pad|laminate|floor)/i.test(text)) {
      return 'Flooring';
    }
    return 'Other Scope';
  }, []);

  const stackOrder = useMemo(() => ([
    'Tear Out / Demolition',
    'Mitigation / Dryout',
    'Reset / Reinstall',
    'Replace / Install',
    'Cleaning / Remediation',
    'Paint / Finish',
    'Flooring',
    'Other Scope',
  ]), []);

  const getResultStackMeta = useCallback((result) => {
    const matchKey = result?.Matches?.[0] ? Object.keys(result.Matches[0])[0] : null;
    const match = matchKey ? result.Matches[0][matchKey] : null;
    const doc1Id =
      match?.['User Modified']?.doc1?.ids?.[0] ||
      match?.['System Detected']?.doc1?.ids?.[0];
    const lineItem = doc1Id ? lineItemMap[doc1Id] : null;
    const description = getDescriptionFromLineItem(lineItem);
    const zone = lineItem?.userModifiedZone || lineItem?.detectedZone || 'Unknown Zone';
    const label = getScopeStackLabel(description);
    const orderIndex = stackOrder.indexOf(label);
    return {
      label,
      orderIndex: orderIndex === -1 ? 999 : orderIndex,
      description,
      zone,
    };
  }, [getDescriptionFromLineItem, getScopeStackLabel, lineItemMap, stackOrder]);

  const mergeUniqueIds = useCallback((currentIds = [], nextIds = []) => {
    const merged = new Set([...(currentIds || []), ...(nextIds || [])]);
    return Array.from(merged);
  }, []);

  const usedDoc2Ids = useMemo(() => {
    const ids = new Set();
    data?.results?.forEach(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      result.Matches[0][matchKey]["User Modified"]?.doc2?.ids?.forEach(id => ids.add(id));
    });
    return ids;
  }, [data?.results]);

  const unusedInsLineItems = useMemo(() => {
    return data?.cleanParsedInsData?.lineItems?.filter(item => {
      const id = Object.keys(item)[0];
      return !usedDoc2Ids.has(id);
    });
  }, [data?.cleanParsedInsData, usedDoc2Ids]);

  const doc1Zones = useMemo(() => data?.cleanParsedEstData?.userModifiedZones
    ? Object.keys(data.cleanParsedEstData.userModifiedZones)
    : [], [data?.cleanParsedEstData]);

  const lineItemUsageMap = useMemo(() => {
    const usageMap = {};
    data?.results?.forEach(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      const userModified = result.Matches[0][matchKey]?.["User Modified"];
      if (!userModified) return;
      [...(userModified.doc1?.ids || []), ...(userModified.doc2?.ids || [])]
        .forEach(id => { usageMap[id] = (usageMap[id] || 0) + 1; });
    });
    return usageMap;
  }, [data?.results]);

  const filteredResults = useMemo(() => {
    if (!data?.results) return [];
    if (designationFilter === "All" && zoneFilter.length === 0) return data.results;
    return data.results.filter(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      const match = result.Matches[0][matchKey];

      const designation = match?.["User Modified"]?.Designation || "No-Match";
      if (designationFilter !== "All" && designation !== designationFilter) return false;

      if (zoneFilter.length > 0) {
        const doc1Ids = match?.["User Modified"]?.doc1?.ids || [];
        const zonesForIds = doc1Ids.map(id => lineItemMap[id]?.userModifiedZone).filter(Boolean);
        if (!zonesForIds.some(z => zoneFilter.includes(z))) return false;
      }

      return true;
    });
  }, [data?.results, designationFilter, zoneFilter, lineItemMap]);

  const displayResults = useMemo(() => {
    if (!stackSimilarItems) return filteredResults;

    const grouped = new Map();

    filteredResults.forEach((result, idx) => {
      const matchKey = result?.Matches?.[0] ? Object.keys(result.Matches[0])[0] : null;
      if (!matchKey) return;

      const match = result.Matches[0][matchKey];
      const user = match?.["User Modified"] || {};
      const system = match?.["System Detected"] || {};
      const userDoc1Ids = user?.doc1?.ids || [];
      const userDoc2Ids = user?.doc2?.ids || [];
      const systemDoc1Ids = system?.doc1?.ids || [];
      const systemDoc2Ids = system?.doc2?.ids || [];
      const meta = getResultStackMeta(result);
      const groupLabel = meta?.label || "Other Scope";

      if (!grouped.has(groupLabel)) {
        const syntheticMatchKey = `stack_${groupLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
        grouped.set(groupLabel, {
          id: `stack-${idx}-${syntheticMatchKey}`,
          isStackedAggregate: true,
          stackMeta: {
            label: groupLabel,
            sourceResultIds: [result.id],
          },
          Matches: [{
            [syntheticMatchKey]: {
              ...match,
              expanded: false,
              "System Detected": {
                ...system,
                doc1: { ...(system?.doc1 || {}), ids: [...systemDoc1Ids] },
                doc2: { ...(system?.doc2 || {}), ids: [...systemDoc2Ids] },
              },
              "User Modified": {
                ...user,
                doc1: { ...(user?.doc1 || {}), ids: [...userDoc1Ids] },
                doc2: { ...(user?.doc2 || {}), ids: [...userDoc2Ids] },
              },
            },
          }],
        });
        return;
      }

      const groupedResult = grouped.get(groupLabel);
      const groupedMatchKey = Object.keys(groupedResult.Matches[0])[0];
      const groupedMatch = groupedResult.Matches[0][groupedMatchKey];
      const groupedUser = groupedMatch["User Modified"] || {};
      const groupedSystem = groupedMatch["System Detected"] || {};

      groupedMatch["System Detected"] = {
        ...groupedSystem,
        doc1: {
          ...(groupedSystem?.doc1 || {}),
          ids: mergeUniqueIds(groupedSystem?.doc1?.ids || [], systemDoc1Ids),
        },
        doc2: {
          ...(groupedSystem?.doc2 || {}),
          ids: mergeUniqueIds(groupedSystem?.doc2?.ids || [], systemDoc2Ids),
        },
      };

      groupedMatch["User Modified"] = {
        ...groupedUser,
        doc1: {
          ...(groupedUser?.doc1 || {}),
          ids: mergeUniqueIds(groupedUser?.doc1?.ids || [], userDoc1Ids),
        },
        doc2: {
          ...(groupedUser?.doc2 || {}),
          ids: mergeUniqueIds(groupedUser?.doc2?.ids || [], userDoc2Ids),
        },
      };

      const priorDesignation = groupedUser?.Designation;
      const nextDesignation = user?.Designation;
      if (priorDesignation && nextDesignation && priorDesignation !== nextDesignation) {
        groupedMatch["User Modified"].Designation = "Similar";
      }

      groupedMatch["Mark For Export"] =
        Boolean(groupedMatch["Mark For Export"]) || Boolean(match?.["Mark For Export"]);

      groupedResult.stackMeta.sourceResultIds.push(result.id);
    });

    return Array.from(grouped.values()).map((result) => {
      const matchKey = result?.Matches?.[0] ? Object.keys(result.Matches[0])[0] : null;
      const match = matchKey ? result.Matches[0][matchKey] : null;
      const doc1Count = match?.["User Modified"]?.doc1?.ids?.length || 0;
      const doc2Count = match?.["User Modified"]?.doc2?.ids?.length || 0;
      return {
        ...result,
        stackMeta: {
          ...result.stackMeta,
          doc1Count,
          doc2Count,
        },
      };
    });
  }, [filteredResults, getResultStackMeta, mergeUniqueIds, stackSimilarItems]);

  const activeLineItemUsageMap = useMemo(() => {
    if (!stackSimilarItems) return lineItemUsageMap;
    const usageMap = {};
    displayResults.forEach(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      const userModified = result.Matches[0][matchKey]?.["User Modified"];
      if (!userModified) return;
      [...(userModified.doc1?.ids || []), ...(userModified.doc2?.ids || [])]
        .forEach(id => { usageMap[id] = (usageMap[id] || 0) + 1; });
    });
    return usageMap;
  }, [displayResults, lineItemUsageMap, stackSimilarItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [designationFilter, zoneFilter, filteredResults.length, stackSimilarItems]);
  const totalPages = Math.max(1, Math.ceil((displayResults?.length || 0) / pageSize));
  const paginatedResults = useMemo(() => {
    if (!displayResults) return [];
    const start = (currentPage - 1) * pageSize;
    return displayResults.slice(start, start + pageSize);
  }, [displayResults, currentPage, pageSize]);

  const getMatchId = useCallback((result, isUnmatched = false) => {
    if (isUnmatched) return `match-unmatched-${result.id}`;
    const matchKey = result?.Matches?.[0] ? Object.keys(result.Matches[0])[0] : null;
    const match = matchKey ? result.Matches[0][matchKey] : null;
    const fallbackId = match?.['System Detected']?.doc1?.ids?.[0]
      || match?.['System Detected']?.doc2?.ids?.[0]
      || matchKey;
    return `match-${result.id || fallbackId}`;
  }, []);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container.scrollTop !== scrollPositionRef.current) {
      container.scrollTop = scrollPositionRef.current;
    }
  }, [data, currentPage, displayResults.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      scrollPositionRef.current = container.scrollTop;
    };
    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [currentPage, displayResults.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let rafId = null;
    const scheduleRestore = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (container.scrollTop !== scrollPositionRef.current) {
          container.scrollTop = scrollPositionRef.current;
        }
      });
    };
    const ro = new ResizeObserver(scheduleRestore);
    const mo = new MutationObserver(scheduleRestore);
    ro.observe(container);
    mo.observe(container, { childList: true, subtree: true, characterData: true });
    return () => {
      ro.disconnect();
      mo.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [currentPage, displayResults.length]);

  const PaginationControls = ({ location = 'top' }) => {
    const isSticky = location === 'sticky';
    const containerStyle = isSticky ? {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 14px',
      position: 'fixed',
      bottom: '12px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1200,
      background: 'rgba(255,255,255,0.98)',
      boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
      borderRadius: 10,
      minWidth: 420,
    } : { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px 16px', marginTop: '8px' };

    const btnStyle = {
      padding: '6px 14px',
      backgroundColor: '#2B9EA3',
      color: '#fff',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: '0.95rem',
      boxShadow: '0 4px 10px rgba(0,0,0,0.12)'
    };

    const disabledBtnStyle = { ...btnStyle, opacity: 0.45, cursor: 'not-allowed' };

    return (
      <div style={containerStyle} data-pagination-location={location}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={currentPage === 1 ? disabledBtnStyle : btnStyle}
            aria-label="Previous page"
          >
            Prev
          </button>
          
          <div style={{ fontSize: '0.95rem', color: '#426767', fontWeight: 700, margin: '0px 4px' }}>
            Page {currentPage} of {totalPages}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={currentPage === totalPages ? disabledBtnStyle : btnStyle}
            aria-label="Next page"
          >
            Next
          </button>
        </div>


      </div>
    );
  };

  const handleToggleAllExport = useCallback(async () => {
    if (!data?.results) return;
    setLoadingExport(true);
    const newValue = !selectAllForExport;
    const updatedResults = data.results.map(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      result.Matches[0][matchKey]["Mark For Export"] = newValue;
      return result;
    });
    setData(prev => prev ? { ...prev, results: updatedResults } : prev);
    setUnmatchedExportMap((prev) => {
      const next = { ...prev };
      (unusedInsLineItems || []).forEach((item) => {
        const id = Object.keys(item)[0];
        next[id] = newValue;
      });
      return next;
    });
    setSelectAllForExport(newValue);
    await saveResults(data.resultsID, updatedResults);
    setLoadingExport(false);
  }, [data, selectAllForExport, saveResults, unusedInsLineItems]);
  
  const handleToggleAllExpand = useCallback(async () => {
    if (!data?.results) return;

    setLoadingExpand(true);
    const newValue = !selectAllExpanded;

    const updatedResults = data.results.map(result => {
      const matchKey = Object.keys(result.Matches[0])[0];
      result.Matches[0][matchKey].expanded = newValue;
      return result;
    });

    setData(prev => prev ? { ...prev, results: updatedResults } : prev);
    setUnmatchedExpandedMap((prev) => {
      const next = { ...prev };
      (unusedInsLineItems || []).forEach((item) => {
        const id = Object.keys(item)[0];
        next[id] = newValue;
      });
      return next;
    });
    setSelectAllExpanded(newValue);

    await saveResults(data.resultsID, updatedResults);
    setLoadingExpand(false);
  }, [data, selectAllExpanded, saveResults, unusedInsLineItems]);

  const onSaveMatchUpdateCallback = useCallback((resultId, updatedSystem) => {
    saveMatchResults(resultId, updatedSystem, setData, saveResults);
  }, [setData, saveResults]);

  const onSaveMatchNotesCallback = useCallback((resultId, newNotes) => {
    saveMatchNotes(resultId, newNotes, setData, saveResults);
  }, [setData, saveResults]);

  const onSaveMarkForExportCallback = useCallback((resultId, val) => {
    saveMarkForExport(resultId, val, setData, saveResults);
  }, [setData, saveResults]);

  const onSaveLockClickCallback = useCallback((resultId, lineItemId) => {
    saveLock(resultId, lineItemId, setData, comparisonID);
  }, [setData, comparisonID]);

  const onRevertMatchCallback = useCallback((resultId) => {
    revertMatch(resultId, setData, comparisonID);
  }, [setData, comparisonID]);

  const toggleDoc = useCallback((docKey) => {
    setVisibleDocs(prev => ({ ...prev, [docKey]: !prev[docKey] }));
  }, []);

  const setCleanParsedEstData = useCallback((updated) => {
    setData(prev => prev ? { ...prev, cleanParsedEstData: updated } : prev);
  }, []);

  const setCleanParsedInsData = useCallback((updated) => {
    setData(prev => prev ? { ...prev, cleanParsedInsData: updated } : prev);
  }, []);

  const MemoCollapsed = useMemo(() => React.memo(CollapsedMatchBlock), []);
  const MemoExpanded = useMemo(() => React.memo(ExpandedMatchBlock), []);

  const MatchItem = ({ result, isUnmatched = false, matchId }) => {
    const ref = useRef();
    const [stackExpanded, setStackExpanded] = useState(false);
    const isStackedAggregate = Boolean(result?.isStackedAggregate);
    const unmatchedId = isUnmatched ? String(result?.id || '') : null;
    const matchKey = isUnmatched
      ? null
      : Object.keys(result.Matches[0])[0];
    const match = isUnmatched
      ? result.match
      : result.Matches[0][matchKey];
    const isExpanded = isUnmatched
      ? Boolean(unmatchedExpandedMap[unmatchedId])
      : isStackedAggregate
        ? stackExpanded
        : Boolean(match?.expanded);
    const renderedMatch = useMemo(() => {
      if (!match) return match;
      if (isUnmatched) {
        return {
          ...match,
          expanded: isExpanded,
          "Mark For Export": Boolean(unmatchedExportMap[unmatchedId]),
        };
      }
      if (isStackedAggregate) {
        return {
          ...match,
          expanded: isExpanded,
        };
      }
      return match;
    }, [isExpanded, isStackedAggregate, isUnmatched, match, unmatchedExportMap, unmatchedId]);
    const toggleMatchExpanded = () => {
      if (isUnmatched) {
        setUnmatchedExpandedMap((prev) => ({ ...prev, [unmatchedId]: !prev[unmatchedId] }));
        return;
      }
      if (isStackedAggregate) {
        setStackExpanded(prev => !prev);
        return;
      }
      toggleExpanded(result.id, setData, saveResults);
    };
    const showStackReadOnlyNotice = () => {
      alert('Stacked view is read-only. Click "UNSTACK LINE ITEMS" to edit individual matches.');
    };
    return (
      <div
        ref={ref}
        id={matchId}
        data-match-id={matchId}
        className={isUnmatched ? 'match-collapse unmatched-block' : 'match-collapse'}
      >
        {isExpanded ? (
          <MemoExpanded
            match={renderedMatch}
            cleanParsedEstData={data.cleanParsedEstData}
            setCleanParsedEstData={setCleanParsedEstData}
            cleanParsedInsData={data.cleanParsedInsData}
            setCleanParsedInsData={setCleanParsedInsData}
            headersOne={data.headersOne}
            headersTwo={data.headersTwo}
            docname1={data.doc1_name}
            docname2={data.doc2_name}
            claimId={data.compID}
            visibleDocs={visibleDocs}
            toggleDoc={toggleDoc}
            onToggle={toggleMatchExpanded}
            onSaveMatchUpdate={(updatedSystem) => {
              if (isStackedAggregate) {
                showStackReadOnlyNotice();
                return;
              }
              onSaveMatchUpdateCallback(result.id, updatedSystem);
            }}
            onSaveMatchNotes={(newNotes) => {
              if (isStackedAggregate) {
                showStackReadOnlyNotice();
                return;
              }
              onSaveMatchNotesCallback(result.id, newNotes);
            }}
            onSaveMarkForExport={(val) => {
              if (isStackedAggregate) {
                showStackReadOnlyNotice();
                return;
              }
              if (isUnmatched) {
                setUnmatchedExportMap((prev) => ({ ...prev, [unmatchedId]: val }));
                return;
              }
              onSaveMarkForExportCallback(result.id, val);
            }}
            onSaveLockClick={(lineItemId) => {
              if (isStackedAggregate) {
                showStackReadOnlyNotice();
                return;
              }
              onSaveLockClickCallback(result.id, lineItemId);
            }}
            doc2NoMatch={isUnmatched}
            onRevertMatch={() => {
              if (isStackedAggregate) {
                showStackReadOnlyNotice();
                return;
              }
              onRevertMatchCallback(result.id);
            }}
            resultsID={data.resultsID}
            lineItemUsageMap={activeLineItemUsageMap}
          />
        ) : (
          <MemoCollapsed
            match={renderedMatch}
            cleanParsedEstData={data.cleanParsedEstData}
            setCleanParsedEstData={setCleanParsedEstData}
            cleanParsedInsData={data.cleanParsedInsData}
            setCleanParsedInsData={setCleanParsedInsData}
            headersOne={data.headersOne}
            headersTwo={data.headersTwo}
            docname1={data.doc1_name}
            docname2={data.doc2_name}
            claimId={data.compID}
            userId={data.claimData?.user_id}
            filename1={data.files?.[0]}
            filename2={data.files?.[1]}
            visibleDocs={visibleDocs}
            toggleDoc={toggleDoc}
            onToggle={toggleMatchExpanded}
            lineItemUsageMap={activeLineItemUsageMap}
            doc2NoMatch={isUnmatched}
          />
        )}
      </div>
    );
  };

  const handlePDFExport = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
  
    try {
      const selectedUnmatchedIds = (unusedInsLineItems || [])
        .map((item) => Object.keys(item)[0])
        .filter((id) => Boolean(unmatchedExportMap[id]));
      try {
        sessionStorage.setItem(
          `levlr_pdf_unmatched_export_ids_${comparisonID}`,
          JSON.stringify(selectedUnmatchedIds)
        );
      } catch (storageErr) {
        console.warn('Unable to persist unmatched export selection for PDF:', storageErr);
      }

      if (signedUrl) {
        await fetch(`${process.env.REACT_APP_DELETE_PDF}/${userData._id}/${comparisonID}.pdf`, {
          method: 'DELETE',
        });
        setSignedUrl(null);
      }
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.width = '1920px';
      iframe.style.height = '1080px';
      iframe.style.visibility = 'hidden';
      iframe.src = `${window.location.origin}/restructuredResultspdf/${comparisonID}`;
      document.body.appendChild(iframe);
      iframe.onload = async () => {
        try {
          const waitForContent = () =>
            new Promise((resolve, reject) => {
              let attempts = 0;
              const maxAttempts = 20;
              const check = () => {
                const el = iframe.contentDocument.querySelector('.comparison-block');
                if (el) return resolve();
                if (++attempts > maxAttempts)
                  return reject(new Error('Timeout waiting for React to render'));
                setTimeout(check, 300);
              };
              check();
            });
          await waitForContent();
          const cloned = iframe.contentDocument.body.cloneNode(true);
          cloned.querySelectorAll('[data-pdf-ignore]').forEach(el => el.remove());
          const html = cloned.innerHTML;
          alert('Your PDF is now being generated. You can safely leave this page and return later to view/download it.');
          fetch(`${process.env.REACT_APP_GENERATE_PDF}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              html,
              comparisonID,
              userEmail: userData.email,
              userId: userData._id,
              userFName: userData.name,
              userLName: userData.lastName
            }),
          }).catch(err => console.error('PDF generation queue error:', err));
          const res = await fetch(`${process.env.REACT_APP_GET_PDFS}/${userData._id}/${comparisonID}.pdf`);
          if (res.ok) {
            const awsdata = await res.json();
            if (awsdata?.url) setSignedUrl(awsdata.url);
          }
        } catch (err) {
          console.error('PDF generation error:', err);
          alert('Failed to generate PDF.');
        } finally {
          iframe.remove();
        }
      };
    } catch (err) {
      console.error('Error deleting existing PDF:', err);
      alert('Failed to delete existing PDF before regenerating.');
    }
  }, [comparisonID, isLoading, signedUrl, unmatchedExportMap, unusedInsLineItems, userData._id, userData.email]);

  const handleCSVExport = useCallback(() => {
    if (!data?.results?.length || loadingCsvExport) return;
    setLoadingCsvExport(true);
    try {
      const normalizeCsvHeader = (header) => (
        String(header || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
      );

      const getValueByHeader = (values, header) => {
        if (!values || !header) return '';
        const headerLower = String(header).toLowerCase();
        const key = Object.keys(values).find(k => String(k).toLowerCase() === headerLower);
        if (key) return values[key];
        if (headerLower === 'description' || headerLower === 'desc') {
          return values.DESCRIPTION || values.Description || values.DESC || '';
        }
        return '';
      };

      const getIds = (matchDoc) => (matchDoc?.ids || []);

      const rows = data.results.map((result, idx) => {
        const matchKey = result?.Matches?.[0] ? Object.keys(result.Matches[0])[0] : '';
        const match = matchKey ? result.Matches[0][matchKey] : {};
        const system = match?.['System Detected'] || {};
        const user = match?.['User Modified'] || {};

        const doc1Ids = getIds(user?.doc1);
        const doc2Ids = getIds(user?.doc2);
        const doc1Items = doc1Ids.map(id => lineItemMap[id]).filter(Boolean);
        const doc2Items = doc2Ids.map(id => insLineItemMap[id]).filter(Boolean);

        const row = {
          row_type: 'matched_result',
          comparison_id: data?.compID || comparisonID || '',
          results_id: data?.resultsID || '',
          result_index: idx + 1,
          result_id: result?.id || '',
          match_key: matchKey,
          designation_system: system?.Designation || '',
          designation_user: user?.Designation || '',
          mark_for_export: Boolean(match?.['Mark For Export']) ? 'true' : 'false',
          notes: user?.Notes || '',
          doc1_ids: doc1Ids.join('|'),
          doc2_ids: doc2Ids.join('|'),
          doc1_zones: doc1Items.map(it => it?.userModifiedZone || it?.detectedZone || '').filter(Boolean).join(' | '),
          doc2_zones: doc2Items.map(it => it?.userModifiedZone || it?.detectedZone || '').filter(Boolean).join(' | '),
          doc1_descriptions: doc1Items.map(getDescriptionFromLineItem).filter(Boolean).join(' || '),
          doc2_descriptions: doc2Items.map(getDescriptionFromLineItem).filter(Boolean).join(' || '),
          doc1_line_items_json: JSON.stringify(doc1Items),
          doc2_line_items_json: JSON.stringify(doc2Items),
          system_detected_json: JSON.stringify(system),
          user_modified_json: JSON.stringify(user),
          match_payload_json: JSON.stringify(match),
        };

        (data?.headersOne || []).forEach((header) => {
          const csvKey = `doc1_${normalizeCsvHeader(header)}`;
          row[csvKey] = doc1Items
            .map(item => getValueByHeader(item?.values, header))
            .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
            .join(' || ');
        });

        (data?.headersTwo || []).forEach((header) => {
          const csvKey = `doc2_${normalizeCsvHeader(header)}`;
          row[csvKey] = doc2Items
            .map(item => getValueByHeader(item?.values, header))
            .filter(v => v !== null && v !== undefined && String(v).trim() !== '')
            .join(' || ');
        });

        return row;
      });

      unusedInsLineItems?.forEach((item) => {
        const id = Object.keys(item)[0];
        const li = item[id];
        const markForExport = Boolean(unmatchedExportMap[id]);
        const row = {
          row_type: 'unmatched_doc2_line_item',
          comparison_id: data?.compID || comparisonID || '',
          results_id: data?.resultsID || '',
          result_index: '',
          result_id: '',
          match_key: '',
          designation_system: 'No-Match',
          designation_user: 'No-Match',
          mark_for_export: markForExport ? 'true' : 'false',
          notes: '',
          doc1_ids: '',
          doc2_ids: id,
          doc1_zones: '',
          doc2_zones: li?.userModifiedZone || li?.detectedZone || '',
          doc1_descriptions: '',
          doc2_descriptions: getDescriptionFromLineItem(li),
          doc1_line_items_json: '[]',
          doc2_line_items_json: JSON.stringify([li]),
          system_detected_json: '',
          user_modified_json: '',
          match_payload_json: '',
        };

        (data?.headersTwo || []).forEach((header) => {
          const csvKey = `doc2_${normalizeCsvHeader(header)}`;
          row[csvKey] = getValueByHeader(li?.values, header);
        });

        rows.push(row);
      });

      if (!rows.length) {
        return;
      }

      const columns = [];
      rows.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (!columns.includes(key)) columns.push(key);
        });
      });

      const escapeCsv = (val) => {
        const str = val === null || val === undefined ? '' : String(val);
        if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
        return str;
      };

      const csv = [
        columns.join(','),
        ...rows.map(row => columns.map(col => escapeCsv(row[col])).join(','))
      ].join('\n');

      const safeBase = (name, fallback) => {
        const base = String(name || '').replace(/\.[^/.]+$/, '');
        const cleaned = base.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
        return cleaned || fallback;
      };

      const sourceOne = safeBase(data?.doc1_name || data?.files?.[0], 'doc1');
      const sourceTwo = safeBase(data?.doc2_name || data?.files?.[1], 'doc2');
      const filename = `${sourceOne}__${sourceTwo}_comparison_results.csv`;

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
      alert('Failed to export CSV.');
    } finally {
      setLoadingCsvExport(false);
    }
  }, [
    comparisonID,
    data,
    getDescriptionFromLineItem,
    insLineItemMap,
    lineItemMap,
    loadingCsvExport,
    unmatchedExportMap,
    unusedInsLineItems
  ]);
  
  return (
    <div className="outer-container restructured-results">
      <MainWrapper />
      <div className="right-container">
        <div
          className="comparison-results-container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            position: 'absolute',
            top: `${resultsTopOffset}px`,
            left: '4px',
            right: '4px',
            width: 'auto',
            maxWidth: 'none',
            bottom: '4px',
            overflow: 'hidden',
          }}
        >
          <div className="restructured-header-controls">
            <div className="restructured-filter-row">
              <Filters selectedFilter={designationFilter} onChange={setDesignationFilter} />
              <ZoneFilter zones={doc1Zones} selectedZones={zoneFilter} onChange={setZoneFilter} />
            </div>
            <div className="restructured-export-row">
              <NukeButton inline onClick={() => revertAllMatches(setData, comparisonID)} />
              <button
                onClick={handlePDFExport}
                disabled={isLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 10px',
                  height: '36px',
                  backgroundColor: isLoading ? '#5b5d5d' : '#49a7ab',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  fontFamily: 'inherit',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={PDFExport}
                  alt="PDF"
                  style={{ width: '24px', height: 'auto', objectFit: 'contain', flexShrink: 0 }}
                />
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                  {isLoading ? 'Generating PDF' : signedUrl ? 'Regenerate PDF' : 'Generate PDF'}
                  {isLoading && (
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '3px solid #fff',
                        borderTop: '3px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </span>
              </button>
              <a
                href={signedUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { if (!signedUrl) e.preventDefault(); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 10px',
                  height: '36px',
                  backgroundColor: '#49a7ab',
                  color: '#fff',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  fontFamily: 'inherit',
                  cursor: signedUrl ? 'pointer' : 'not-allowed',
                  overflow: 'hidden',
                  opacity: signedUrl ? 1 : 0.3,
                  justifyContent: 'center',
                  border: 'none',
                  borderRadius: '4px',
                }}
              >
                <img
                  src={pdfIcon}
                  alt="PDF Icon"
                  style={{ width: '24px', height: 'auto', objectFit: 'contain', flexShrink: 0 }}
                />
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                  DOWNLOAD PDF
                </span>
              </a>
              <button
                onClick={handleCSVExport}
                disabled={loadingCsvExport || !data?.results?.length}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 12px',
                  height: '36px',
                  backgroundColor: '#49a7ab',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  fontFamily: 'inherit',
                  cursor: loadingCsvExport || !data?.results?.length ? 'not-allowed' : 'pointer',
                  opacity: loadingCsvExport || !data?.results?.length ? 0.45 : 1,
                }}
                title="Export all comparison results to CSV"
              >
                {loadingCsvExport ? 'EXPORTING CSV...' : 'EXPORT CSV'}
              </button>
            </div>
            <style>
              {`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: (visibleDocs.doc1 || visibleDocs.doc2) ? 16 : 2
            }}
          >
            {visibleDocs.doc1 && data?.files?.[0] && (
              <PDFViewer userId={data.claimData.user_id} filename={data.files[0]} onClose={() => toggleDoc('doc1')} docType="doc1" />
            )}
            {visibleDocs.doc2 && data?.files?.[1] && (
              <PDFViewer userId={data.claimData.user_id} filename={data.files[1]} onClose={() => toggleDoc('doc2')} docType="doc2" />
            )}
          </div>
          {data?.doc1_name && data?.doc2_name && (
            <div
              className="column-headers"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                marginBottom: 4,
                padding: '4px 8px',
                boxSizing: 'border-box',
                borderBottom: '1px solid #42999d',
              }}
            >
              {[0, 1].map(i => {
                const name = i === 0 ? data.doc1_name : data.doc2_name;
                const price = i === 0 ? data?.estPriceList : data?.insPriceList;
                const truncatedName = name.length > 30 ? name.slice(0, 27) + '...' : name;
                return (
                  <div
                    key={i}
                    style={{
                      textAlign: 'left',
                      width: '50%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      paddingLeft: i === 0 ? '6px' : '12px',
                      borderRight: i === 0 ? '1px solid #ccc' : '0',
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        color: '#42999d',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                      }}
                      title={name}
                    >
                      {truncatedName}
                    </h4>
                    {price && (
                      <i
                        style={{
                          margin: 0,
                          color: '#839299',
                          fontWeight: '600',
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontStyle: 'normal',
                        }}
                      >
                        Price List: {price}
                        {data?.estPriceList && data?.insPriceList && (
                          <img
                            src={data.estPriceList !== data.insPriceList ? yieldIcon : GoIcon}
                            alt={data.estPriceList !== data.insPriceList ? 'yield icon' : 'go icon'}
                            style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                          />
                        )}
                      </i>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <style>
            {`
              .match-placeholder { width: 100%; min-height: 110px; background: transparent; }
            `}
          </style>
          <div ref={paginationRef} className="restructured-pagination-row">
            <PaginationControls location="top" />
          </div>
          <div className="restructured-match-actions">
            <label
              htmlFor="select-all-export"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600,
                color: '#3f8f93',
                whiteSpace: 'nowrap'
              }}
            >
              <input
                type="checkbox"
                checked={selectAllForExport}
                onChange={handleToggleAllExport}
                id="select-all-export"
                disabled={loadingExport}
              />
              {loadingExport ? 'Updating...' : 'Mark All for Export'}
            </label>
            <button
              onClick={() => setStackSimilarItems(prev => !prev)}
              style={{
                backgroundColor: stackSimilarItems ? '#2f7c80' : '#489EA3',
                color: 'white',
                fontWeight: 700,
                border: 'none',
                borderRadius: '4px',
                padding: '6px 10px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              title="Combine similar claim-scope line items into a single stacked match card."
            >
              {stackSimilarItems ? 'UNSTACK LINE ITEMS' : 'STACK SIMILAR LINE ITEMS'}
            </button>
            <button
              onClick={handleToggleAllExpand}
              disabled={loadingExpand}
              style={{
                backgroundColor: '#489EA3',
                color: 'white',
                fontWeight: 700,
                border: 'none',
                borderRadius: '4px',
                padding: '6px 10px',
                cursor: loadingExpand ? 'not-allowed' : 'pointer',
                opacity: loadingExpand ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              {loadingExpand ? (
                <span>Working...</span>
              ) : (
                <>
                  <img
                    src={selectAllExpanded ? collapseIcon : expandIcon}
                    alt={selectAllExpanded ? "COLLAPSE ALL" : "EXPAND ALL"}
                    style={{ width: 14, height: 14 }}
                  />
                  {selectAllExpanded ? "COLLAPSE ALL" : "EXPAND ALL"}
                </>
              )}
            </button>
          </div>
          <div
            id="pairings"
            className="pairings-container"
            style={{ marginTop: 0, overflowAnchor: 'none', flex: 1, minHeight: 0 }}
            ref={scrollContainerRef}
          >
            <div className="pairings-scroll-body">
              <div className={loadingStatus}></div>
                {paginatedResults.map(result => {
                  const matchId = getMatchId(result);
                  const stackMeta = stackSimilarItems ? result?.stackMeta : null;
                  return (
                    <React.Fragment key={matchId}>
                      {stackSimilarItems && stackMeta?.label && (
                        <div
                          style={{
                            margin: '12px 8px 6px',
                            padding: '6px 10px',
                            background: '#eef7f8',
                            borderLeft: '4px solid #2f7c80',
                            color: '#2f7c80',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            display: 'inline-flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <span>{stackMeta.label}</span>
                          <span style={{ color: '#4f7f82', fontWeight: 600 }}>
                            {stackMeta.doc1Count || 0} est | {stackMeta.doc2Count || 0} ins
                          </span>
                        </div>
                      )}
                      <MatchItem result={result} matchId={matchId} />
                    </React.Fragment>
                  );
                })}
              {data?.claimData?.doc2_name && (
                <h4
                  style={{
                    fontSize: '24px',
                    color: '#42999d',
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    margin: '32px auto 64px auto'
                  }}
                >
                  No Matches From: <i style={{ color: '#839299' }}>{data.claimData.doc2_name}</i>
                </h4>
              )}
              {unusedInsLineItems?.map(item => {
                const id = Object.keys(item)[0];
                const mockMatch = {
                  expanded: Boolean(unmatchedExpandedMap[id]),
                  'Mark For Export': Boolean(unmatchedExportMap[id]),
                  'System Detected': { doc1: { ids: [] }, doc2: { ids: [id] }, Designation: 'No-Match' },
                  'User Modified': { doc1: { ids: [] }, doc2: { ids: [id] }, Designation: 'No-Match', Notes: '', MarkForExport: false }
                };
                const matchId = getMatchId({ id }, true);
                return (
                  <MatchItem key={matchId} matchId={matchId} result={{ id, match: mockMatch }} isUnmatched={true} />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestructuredResults;
